import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Col, Drawer, Row, Typography } from 'antd';

import { Container, MessageBox, Spinner } from '@/components/Elements';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import StepProgress from '@/components/StepProgress/StepProgress';
import useScrollToTop from '@/hooks/useScrollTop';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import { useGetHouseholdExpenditureQuery } from '@/pages/profile/api/profileAPI';
import {
  useGetPurchaseApplicationSummaryQuery,
  useGetPurchaseStepDetailQuery,
} from '@/pages/mortgage/apis/purchaseAPI';
import {
  useFetchFxRatesQuery,
  useFetchHemBenchmarksQuery,
} from '@/pages/blueprint-lite/apis/blueprintApi';
import {
  useCreateFactfindMutation,
  useGetFactfindListQuery,
  useUpdateFactfindMutation,
} from '@/pages/mortgage/apis/factfindAPI';

import MeetingModal from '../../components/PurchaseMeetingModal';
import NextStepModal from '../../components/NextStepModal';
import {
  EXPENSE_GROUPS,
  EXPENSE_KEYS,
  ExpenseKey,
  FACT_FIND_STEPS,
  HEM_API_TO_EXPENSE,
} from './constant';
import {
  EMPTY_ADDRESS,
  FactFindFormType,
  factFindSchema,
  prefillFromPrevious,
  STEP_FIELDS,
} from './schema';
import {
  AssetsStep,
  ExpensesStep,
  LowTotalAlert,
  OftenMissed,
  PersonalStep,
  toAmount,
} from './Steps';
import FactFindSections from './FactFindSections';
import { factFindView } from './view';
import { jobStartDate } from './schema';
import {
  applicantGrossAnnualAud,
  findHemMinimum,
  HemBenchmarkState,
  hemStatus,
} from './hem';

const DEFAULT_VALUES: Partial<FactFindFormType> = {
  firstName: '',
  middleName: '',
  lastName: '',
  currentAddress: EMPTY_ADDRESS,
  currentAddressStartDate: '',
  previousAddresses: [],
  currentOccupation: '',
  currentEmployer: '',
  currentEmployerAddress: EMPTY_ADDRESS,
  currentEmploymentStartDate: '',
  previousEmployments: [],
  expenseCurrency: 'AUD',
  declaration: false,
};

const FactFind = () => {
  const { id = '', step: stepParam } = useParams();
  const step = Number(stepParam);
  const navigate = useNavigate();
  useScrollToTop();

  const [reviewOpen, setReviewOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const {
    data: factfinds,
    isLoading,
    isError: listError,
    refetch: refetchList,
  } = useGetFactfindListQuery();
  const existing = factfinds?.find(
    (f) => f.assessmentType === 'purchase' && f.assessmentId === id,
  );
  // The client's latest submitted fact-find from an earlier application:
  // a new application's fact-find starts from it so they only update changes.
  const previous = factfinds
    ?.filter(
      (f) =>
        f.assessmentType === 'purchase' &&
        f.assessmentId !== id &&
        f.data?.status === 'submitted',
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  const { data: userData, isLoading: userLoading } =
    useFetchUserDataQuery(undefined);
  // Prefill sources: what the client already told us elsewhere.
  const { data: hem, isLoading: hemLoading } =
    useGetHouseholdExpenditureQuery();
  const { data: income, isLoading: incomeLoading } =
    useGetPurchaseStepDetailQuery(
      { application_id: id, step: 'income_detail' },
      { skip: !id },
    );
  // HEM benchmark for the expenses step: broker-copilot feed + the
  // applicant's household (Personal Details) and gross income.
  const {
    data: summary,
    isError: summaryError,
    isLoading: summaryLoading,
  } = useGetPurchaseApplicationSummaryQuery(
    { application_id: id },
    { skip: !id },
  );
  const {
    data: hemRows,
    isError: hemError,
    isLoading: hemRowsLoading,
  } = useFetchHemBenchmarksQuery();
  const { data: fx, isLoading: fxLoading } = useFetchFxRatesQuery();
  // undefined while loading; otherwise a minimum or the reason there's none.
  const hemBenchmark = useMemo((): HemBenchmarkState | undefined => {
    if (summaryLoading || hemRowsLoading || fxLoading) return undefined;
    if (hemError || !hemRows)
      return {
        unavailable:
          'The living expense benchmark could not be loaded right now.',
      };
    const data = summary?.data;
    if (summaryError || !data)
      return {
        unavailable:
          "We couldn't read your purchase application details to find your living expense benchmark.",
      };
    const income = applicantGrossAnnualAud(data.income_detail, fx?.rates);
    if (income === 'no-fx')
      return {
        unavailable:
          "We couldn't convert your income to AUD right now, so the living expense benchmark isn't shown.",
      };
    if (income === 'no-income')
      return {
        unavailable:
          'Add your income to your purchase application to see the living expense benchmark.',
      };
    const minimum = findHemMinimum(
      hemRows,
      data.primary_detail?.marital_status,
      data.primary_detail?.number_of_dependents,
      income,
    );
    return minimum === null
      ? {
          unavailable:
            'A living expense benchmark is not available for your household.',
        }
      : { minimum };
  }, [
    summary,
    summaryError,
    summaryLoading,
    hemRows,
    hemError,
    hemRowsLoading,
    fx,
    fxLoading,
  ]);
  const [expensesPrefilled, setExpensesPrefilled] = useState(false);
  const [prefilledFromPrevious, setPrefilledFromPrevious] = useState(false);
  const [createFactfind, { isLoading: creating }] = useCreateFactfindMutation();
  const [updateFactfind, { isLoading: updating }] = useUpdateFactfindMutation();

  const {
    control,
    formState: { errors },
    getValues,
    reset,
    setValue,
    trigger,
  } = useForm<FactFindFormType>({
    resolver: yupResolver(factFindSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  // Hydrate once. A saved fact-find wins; otherwise prefill from what the
  // client already gave us: profile name, purchase application occupation /
  // employer, and profile household expenditure (AUD per month).
  const recordId = useRef<number>();
  // Once submitted, saves never move it back to draft.
  const submitted = useRef(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    if (isLoading || userLoading || hemLoading || incomeLoading) return;
    // Without the list we can't tell if a fact-find exists; starting fresh
    // would overwrite it on the first save.
    if (listError) return;
    hydrated.current = true;
    if (existing) {
      recordId.current = existing.id;
      submitted.current = existing.data?.status === 'submitted';
      reset({ ...DEFAULT_VALUES, ...existing.data });
      return;
    }
    const incomeData = income?.data?.data as
      | {
          occupation?: string;
          employer?: string;
          jobStartMonth?: string;
          jobStartYear?: string;
        }
      | undefined;
    if (previous) {
      reset({
        ...DEFAULT_VALUES,
        ...prefillFromPrevious(previous.data, incomeData),
      });
      setPrefilledFromPrevious(true);
      return;
    }
    const prefill: Partial<FactFindFormType> = {
      firstName: userData?.data?.firstName ?? '',
      lastName: userData?.data?.lastName ?? '',
      currentOccupation: incomeData?.occupation ?? '',
      currentEmployer: incomeData?.employer ?? '',
      currentEmploymentStartDate: jobStartDate(
        incomeData?.jobStartMonth,
        incomeData?.jobStartYear,
      ),
    };
    const hemDetails: Record<string, unknown> | undefined =
      hem?.data?.hem_details;
    if (hemDetails) {
      Object.entries(HEM_API_TO_EXPENSE).forEach(([apiKey, field]) => {
        const v = hemDetails[apiKey];
        // Profile saves untouched categories as 0: only prefill real
        // amounts so the rest show the 0 placeholder instead of a typed 0.
        if (Number(v) > 0) prefill[field] = String(v);
      });
      prefill.expenseCurrency = 'AUD';
      setExpensesPrefilled(true);
    }
    reset({ ...DEFAULT_VALUES, ...prefill });
  }, [
    existing,
    previous,
    isLoading,
    listError,
    userLoading,
    hemLoading,
    incomeLoading,
  ]);

  // ---- Step 2: often-missed pills + low-total check on Next ----
  const [openGroups, setOpenGroups] = useState(
    EXPENSE_GROUPS.map((g) => g.title),
  );
  const expenseValues = useWatch({ control, name: EXPENSE_KEYS });
  const expenseCurrency = useWatch({ control, name: 'expenseCurrency' });
  const filledExpenses = Object.fromEntries(
    EXPENSE_KEYS.map((k, i) => [
      k,
      String(expenseValues[i] ?? '').trim() !== '',
    ]),
  );
  // TODO(OMT-1745): only HEM-covered categories should count towards this
  // total, pending credit-analyst sign-off; today all 16 expenses count.
  const expenseTotal = expenseValues.reduce((s, v) => s + toAmount(v), 0);
  // TODO(OMT-1745): convert non-AUD expenses to AUD; until then the
  // benchmark check only runs when expenses are entered in AUD.
  const totalIsLow =
    (expenseCurrency || 'AUD') === 'AUD' &&
    !!hemBenchmark &&
    'minimum' in hemBenchmark &&
    hemStatus(expenseTotal, hemBenchmark.minimum) === 'below';
  const lowConfirmed = useRef(false);
  const [lowAlert, setLowAlert] = useState(false);
  const lowTotalPending = () => {
    if (!totalIsLow || lowConfirmed.current) return false;
    setLowAlert(true);
    return true;
  };
  const jumpToExpense = (key: ExpenseKey) => {
    const group = EXPENSE_GROUPS.find((g) => g.keys.includes(key))!.title;
    setOpenGroups((open) => (open.includes(group) ? open : [...open, group]));
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLInputElement>(
        `input[name="${key}"]`,
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus({ preventScroll: true });
    });
  };

  const save = async (status: 'draft' | 'submitted') => {
    if (submitted.current) status = 'submitted';
    const payload = {
      assessmentType: 'purchase' as const,
      assessmentId: id,
      // ponytail: status lives inside the JSON blob; promote to a column
      // on the backend if admins need to filter by it.
      data: { ...getValues(), status },
    };
    const res = recordId.current
      ? await updateFactfind({ id: recordId.current, ...payload }).unwrap()
      : await createFactfind(payload).unwrap();
    recordId.current = res.id;
    if (status === 'submitted') submitted.current = true;
  };

  const goToStep = (n: number) =>
    navigate(`${FACT_FIND_STEPS[n - 1].link}/${id}`);

  // On a failed Next: bring the first field in error into view and focus it.
  const focusFirstError = () =>
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        '.form-body .ant-input-status-error, .form-body .ant-input-affix-wrapper-status-error input, .form-body .ant-select-status-error input, .form-body .ant-picker-status-error input, .form-body .ant-radio-group .ant-radio-input, .form-body .error-text',
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus?.({ preventScroll: true });
    });

  const handleNext = async () => {
    if (!(await trigger(STEP_FIELDS[step]))) {
      focusFirstError();
      return;
    }
    if (step === 2 && lowTotalPending()) return;
    if (step === 3) {
      // Save before reviewing so closing the drawer never loses step 3.
      try {
        await save('draft');
      } catch (err) {
        displayError(err);
        return;
      }
      setReviewOpen(true);
      return;
    }
    try {
      await save('draft');
      goToStep(step + 1);
    } catch (err) {
      displayError(err);
    }
  };

  // Keep what was typed on this step even though it isn't validated yet.
  const handleBack = async () => {
    try {
      await save('draft');
    } catch (err) {
      displayError(err);
    }
    goToStep(step - 1);
  };

  const handleConfirm = async () => {
    // Earlier steps can be edited (or skipped via the URL) after their Next.
    for (const n of [1, 2, 3]) {
      if (!(await trigger(STEP_FIELDS[n]))) {
        setReviewOpen(false);
        goToStep(n);
        return;
      }
    }
    try {
      await save('submitted');
      displaySuccess('Fact-find submitted');
      setReviewOpen(false);
      setDoneOpen(true);
    } catch (err) {
      displayError(err);
    }
  };

  if (![1, 2, 3].includes(step))
    return <Navigate to={`${FACT_FIND_STEPS[0].link}/${id}`} replace />;
  const view = factFindView({
    isLoading,
    listError,
    hasList: !!factfinds,
    submitted: existing?.data?.status === 'submitted',
    postSubmitOpen: doneOpen || meetingOpen,
  });
  if (view === 'loading') return <Spinner />;
  if (view === 'error')
    return (
      <Container width="md">
        <MessageBox>
          We couldn't load your fact-find right now.{' '}
          <Button onClick={refetchList}>Try again</Button>
        </MessageBox>
      </Container>
    );
  if (view === 'summary')
    return (
      <Navigate to={`/mortgage/purchase/summary/${id}?tab=factfind`} replace />
    );

  // Step 2 renders ExpensesStep directly (it takes extra props).
  const StepBody = step === 3 ? AssetsStep : PersonalStep;

  return (
    <StepperFormLayout
      handleSubmit={handleNext}
      handleBackClick={handleBack}
      showBackButton={step > 1}
      nextBtnText={
        step === 3 ? 'Review & Submit' : `Next: ${FACT_FIND_STEPS[step].step}`
      }
      isSubmitLoading={creating || updating}
      footerTop={
        step === 2 ? (
          <>
            {lowAlert && totalIsLow && (
              <LowTotalAlert
                onConfirm={() => {
                  lowConfirmed.current = true;
                  setLowAlert(false);
                  handleNext();
                }}
              />
            )}
            <OftenMissed filled={filledExpenses} onPick={jumpToExpense} />
          </>
        ) : undefined
      }
    >
      <Container width="md">
        <Row style={{ paddingBottom: 20 }}>
          <Col xs={24}>
            <StepProgress
              id={id}
              completedStep={step - 1}
              totalSteps={3}
              stepData={FACT_FIND_STEPS}
            />
            <Typography.Text type="secondary">
              <strong>Step {step} of 3</strong> ·{' '}
              {FACT_FIND_STEPS[step - 1].step}
            </Typography.Text>
          </Col>
        </Row>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          {prefilledFromPrevious && (
            <MessageBox style={{ marginBottom: 20 }}>
              We've prefilled this from your last fact-find. Please check every
              detail and update anything that has changed.
            </MessageBox>
          )}
          {step === 2 && expensesPrefilled && (
            <MessageBox style={{ marginBottom: 20 }}>
              We've prefilled these amounts from the Household Expenditure in
              your profile (AUD per month). Please check them, and update them
              if you change the currency.
            </MessageBox>
          )}
          {step === 2 ? (
            <ExpensesStep
              control={control}
              errors={errors}
              setValue={setValue}
              openGroups={openGroups}
              onOpenGroupsChange={setOpenGroups}
            />
          ) : (
            <StepBody control={control} errors={errors} setValue={setValue} />
          )}
        </form>
      </Container>

      <Drawer
        title="Review Submission"
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        width={980}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button size="large" onClick={() => setReviewOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              loading={creating || updating}
              onClick={handleConfirm}
            >
              Confirm & Submit
            </Button>
          </div>
        }
      >
        {reviewOpen && (
          <FactFindSections
            values={getValues()}
            onEdit={(n) => {
              setReviewOpen(false);
              goToStep(n);
            }}
          />
        )}
      </Drawer>

      <NextStepModal
        open={doneOpen}
        eyebrow="Fact-find submitted"
        description="Your broker now has your fact-find. Uploading your documents next keeps your application moving."
        options={[
          {
            id: 'docs',
            title: 'Go to document upload',
            body: 'Upload the documents your broker needs to assess your application.',
            cta: 'Go to document upload',
            recommended: true,
          },
          {
            id: 'meeting',
            title: 'Book a meeting with a broker',
            body: 'Pick a time to talk through your application with your broker.',
            cta: 'Book a meeting',
          },
          {
            id: 'back',
            title: 'Back to my applications',
            body: 'Return to your purchase applications. Your fact-find summary is available there.',
            cta: 'Go to my applications',
          },
        ]}
        onConfirm={(choice) => {
          setDoneOpen(false);
          // TODO(OMT-1736): "Go to document upload" has no destination yet
          // (the portal has no document upload route); it falls back to the
          // applications list until one exists.
          if (choice === 'meeting') setMeetingOpen(true);
          else navigate('/mortgage/purchase', { replace: true });
        }}
      />
      {/* TODO(OMT-1736): confirm the "Book a meeting" destination; for now it
          opens the existing purchase meeting modal. */}
      <MeetingModal
        isOpen={meetingOpen}
        onClose={() => navigate('/mortgage/purchase', { replace: true })}
        onOk={() => navigate('/mortgage/purchase', { replace: true })}
      />
    </StepperFormLayout>
  );
};

export default FactFind;
